/**
 * Single shared refresh flow so only one POST /api/v1/refresh-token runs at a time.
 * Used by BaseQuery (before RTK requests) and AuthSlice (when GET /api/v1/me returns 401).
 * Prevents double refresh and accidental logout from 401 on second refresh.
 */

const JWT_BUFFER_SECONDS = 15;
const LOG_PREFIX = "[AuthRefresh]";

function getJwtExpiry(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpiredOrExpiringSoon(
  token: string,
  bufferSeconds: number
): boolean {
  const exp = getJwtExpiry(token);
  if (exp === null) return true;
  return Math.floor(Date.now() / 1000) >= exp - bufferSeconds;
}

let refreshPromise: Promise<void> | null = null;

async function refreshAuthToken(): Promise<void> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token");
  }
  const response = await fetch("/api/v1/refresh-token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) {
    throw new Error("Failed to refresh token");
  }
  const data = await response.json();
  if (data.auth_token) {
    localStorage.setItem("auth_token", data.auth_token);
    if (data.refresh_token != null && data.refresh_token !== "") {
      localStorage.setItem("refresh_token", data.refresh_token);
    }
  } else {
    throw new Error("No auth_token in refresh response");
  }
}

/**
 * If auth_token is expired or < 15s left, refresh it once. All callers share the same
 * in-flight refresh so only one POST /api/v1/refresh-token runs. Rejects if refresh fails.
 */
export async function ensureValidToken(): Promise<void> {
  const authToken = localStorage.getItem("auth_token");
  const refreshToken = localStorage.getItem("refresh_token");
  if (!authToken || !refreshToken) {
    return;
  }
  if (!isTokenExpiredOrExpiringSoon(authToken, JWT_BUFFER_SECONDS)) {
    return;
  }
  if (refreshPromise) {
    await refreshPromise;
    return;
  }
  refreshPromise = refreshAuthToken().finally(() => {
    refreshPromise = null;
  });
  await refreshPromise;
}
